import { useMutation, useQuery } from '@apollo/client'
import ActionButton from '@deltek/specification-client/core/Buttons/ActionButton'
import { NavPromptMessageProps } from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    AssignedUser,
    ListingDetail,
} from '@deltek/specification-client/core/Models/listing'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import { User } from '@deltek/specification-client/core/Models/user'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { CircularProgress } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import {
    CopyProductListing,
    DeleteProductListing,
    MoveProductListing,
} from 'bpm/api/graphQL/mutations/productListing.mutation'
import {
    isDeltekUserorAdmin,
    isUserBPMAdmin,
} from 'bpm/pages/ProductsPage/productsUtils'
import useWindow from 'bpm/shared/hooks/useWindow'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import ListingDetailsUserAssignment from './Assignments'
import {
    ApiInput,
    CopyProductForm,
    CopyProductInput,
    CopyProductProps,
    MoveProductInput,
    MoveProductProps,
} from './CopyDelete/CopyProduct'
import CopyMoveDialog from './CopyMoveDialog'
import type {
    ListingHeaderVariants,
    ProductListingDetailForm,
} from './ListingDetailsPage'
import { RoleBasedAcessMatrix } from './ListingDetailsUtils'
import { blankForm, noChange } from './ListingHeader.constants'
import useStyles from './ListingHeader.styles'
import ListingHeaderButton from './ListingHeader/ListingHeaderButton'
import ListingHeaderPopover from './ListingHeader/ListingHeaderPopover'
import ListingLevelVariant from './ListingHeaderVariants/ListingLevelVariant'
import ListingStateVariant from './ListingHeaderVariants/ListingStateVariant'
import ListingStatusVariant from './ListingHeaderVariants/ListingStatusVariant'
import { AddNotesModal } from './ListingNotes/AddNotesModal'
import usePDNhook from './ListingNotes/hooks/usePDNHook'
import {
    noteReasonLabels,
    typeAction,
} from './ListingNotes/ListingNotes.constants'
import {
    removeTempNote,
    resetPDNStore,
} from './ListingNotes/ListingNotes.store'
import { NoteType } from './ListingNotes/ListingNotes.types'
import PDNButton from './ListingNotes/PDNButton'

export interface ListingHeaderProps {
    assignment?: string
    firmId?: string
    editMode: boolean
    listingLevel?: ListingLevel
    isActive?: boolean
    isAddProductActive?: boolean
    onAddProductClick: VoidFunction
    onCancelClick: VoidFunction
    onEditClick: VoidFunction
    onPublishClick: VoidFunction
    onSaveClick: VoidFunction
    onHeaderChanges: (
        field: ListingHeaderVariants,
        value: string | boolean
    ) => void
    submissionStatus?: string
    saveProductListingDetailLoading: boolean
    disableSave?: boolean
    assignedBPM?: Partial<AssignedUser>[] | null
    assignedDeltek?: Partial<AssignedUser>[] | null
    allBPMUsers: Partial<User>[]
    allDeltekUsers: Partial<User>[]
    productInfo?: Partial<ListingDetail>
    handleBPMUsersChange?: (
        /** Event source */
        users: Partial<AssignedUser>[],
        /** New selection */
        valid: boolean
    ) => void
    handleDeltekUsersChange?: (
        /** Event source */
        users: Partial<AssignedUser>[],
        /** New selection */
        valid: boolean
    ) => void
    roleBasedAccess: Partial<RoleBasedAcessMatrix>
    isDeprecated: boolean
    productListingDetailFormProp: ProductListingDetailForm
}

export const ListingHeader: React.FC<ListingHeaderProps> = ({
    isAddProductActive,
    assignment,
    editMode,
    listingLevel,
    isActive,
    onAddProductClick,
    onCancelClick,
    onEditClick,
    onPublishClick,
    onSaveClick,
    onHeaderChanges,
    submissionStatus,
    saveProductListingDetailLoading,
    disableSave,
    assignedBPM,
    assignedDeltek,
    allBPMUsers,
    productInfo,
    allDeltekUsers,
    handleBPMUsersChange,
    handleDeltekUsersChange,
    roleBasedAccess,
    isDeprecated,
    productListingDetailFormProp,
}) => {
    const history = useHistory()
    const classes = useStyles()
    const theme = useTheme()

    const { handleSaveNote, isPDNModalOpen, setIsPDNModalOpen } = usePDNhook()
    const { getWindowDomain, getWindowQuery, getWindowURL } = useWindow()

    const assignedBPMLabel =
        assignedBPM && assignedBPM?.length > 0
            ? assignedBPM.length === 1
                ? `${assignedBPM[0]?.profileInfo?.firstName} ${assignedBPM[0]?.profileInfo?.lastName}`
                : `${assignedBPM[0]?.profileInfo?.firstName} ${assignedBPM[0]?.profileInfo?.lastName
                } (${assignedBPM.length - 1} more...)`
            : assignment
    const assignedDeltekLabel =
        assignedDeltek && assignedDeltek?.length > 0
            ? assignedDeltek.length === 1
                ? `${assignedDeltek[0]?.profileInfo?.firstName} ${assignedDeltek[0]?.profileInfo?.lastName}`
                : `${assignedDeltek[0]?.profileInfo?.firstName} ${assignedDeltek[0]?.profileInfo?.lastName
                } (${assignedDeltek.length - 1} more...)`
            : assignment

    const [tempPDNStore, setTempPDNStore] = useState<{
        actionType: string | keyof typeof noteReasonLabels
        value: [ListingHeaderVariants | string, string | boolean] | never[]
    }>({
        actionType: '',
        value: [],
    })

    const [
        popoverAnchorEl,
        setPopoverAnchorEl,
    ] = React.useState<HTMLButtonElement | null>(null)
    const [popoverVariant, setpopoverVariant] = React.useState<string | null>(
        null
    )
    const [copyOrMoveModalOpen, setCopyOrMoveModalOpen] = React.useState<
        boolean
    >(false)

    const [copyMoveProductForm, setCopyMoveProductForm] = React.useState<
        Partial<CopyProductForm>
    >({ ...blankForm })

    const { showGlobalSnackbar } = useGlobalSnackbar()

    const [copyProduct, { loading: loadingCopyProduct }] = useMutation<
        CopyProductProps,
        { input: CopyProductInput }
    >(CopyProductListing, {
        onCompleted: () => {
            resetPDNStore()
            if (getWindowURL().includes('showBackButton=false')) {
                window.close()
            }
            history.push(`/bpm/products/all?${getWindowQuery()}`)
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message:
                        error?.message ||
                        'Error on Move project product listing',
                    variant: 'error',
                    id: `ProductListingsPage_ProjectProductListingError`,
                },
            })
        },
    })

    const [moveProduct, { loading: loadingMoveProduct }] = useMutation<
        MoveProductProps,
        { input: MoveProductInput }
    >(MoveProductListing, {
        onCompleted: () => {
            resetPDNStore()
            if (getWindowURL().includes('showBackButton=false')) {
                window.close()
            }
            history.push(`/bpm/products/all?${getWindowQuery()}`)
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message:
                        error?.message ||
                        'Error on Move project product listing',
                    variant: 'error',
                    id: `ProductListingsPage_ProjectProductListingError`,
                },
            })
        },
    })

    const downMD = useMediaQuery(theme.breakpoints.down('sm'))
    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )
    const {
        add,
        update,
        submit,
        updateSwatchbox,
        updateBPMLimited,
    } = roleBasedAccess

    const handleListingLevelChange = (value: string) => {
        if (value === productInfo?.type && listingLevel === productInfo?.type) {
            handlePopoverClose()
            return
        }

        if (value === productInfo?.type || !!isAddProductActive) {
            removeTempNote('LISTINGTYPECHANGE')
            onHeaderChanges('listingLevel', value)
            handlePopoverClose()
            return
        }

        setTempPDNStore({
            actionType: 'LISTINGTYPECHANGE',
            value: ['listingLevel', value],
        })

        setIsPDNModalOpen(true)
        handlePopoverClose()
    }

    const handleStatusChange = (value: string) => {
        if (
            (!!productInfo?.isActive && value === 'Active' && !!isActive) ||
            (!productInfo?.isActive && value !== 'Active' && !isActive)
        ) {
            handlePopoverClose()
            return
        }

        if (
            (!!productInfo?.isActive && value === 'Active') ||
            (!productInfo?.isActive && value !== 'Active') ||
            !!isAddProductActive
        ) {
            removeTempNote('DELETEORSTATUSCHANGE')
            onHeaderChanges('status', value === 'Active')
            handlePopoverClose()
            return
        }

        setTempPDNStore({
            actionType: 'STATUSCHANGE',
            value: ['status', value === 'Active'],
        })

        setIsPDNModalOpen(true)
        handlePopoverClose()
    }

    const handleStateChange = (value: string) => {
        onHeaderChanges('state', value)
        handlePopoverClose()
    }

    const {
        isDeltekAdmin,
        isDeltekPublishingAuthor,
        isDeltekAuthor,
    } = isDeltekUserorAdmin(currentUserInfoResponse)

    const { isBPM, isBPMAdmin, isBPMAuthor } = isUserBPMAdmin(
        currentUserInfoResponse
    )

    const isBPMUser = isBPM && (isBPMAdmin || isBPMAuthor)

    const isDeltekAdminOrAuthor =
        isDeltekAdmin || isDeltekPublishingAuthor || isDeltekAuthor
    const canEdit = !!(
        add ||
        update ||
        isDeprecated ||
        updateBPMLimited
    )

    const canInteractWithButtons = !!(
        isDeltekAdminOrAuthor || updateBPMLimited
    )

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (canInteractWithButtons) {
            const button = event.currentTarget
            setPopoverAnchorEl(button)
            setpopoverVariant(button.id)
        }
    }

    const handleCancelCreatePDN = useCallback(() => {
        setIsPDNModalOpen?.(false)
        setTempPDNStore({
            actionType: '',
            value: [],
        })
    }, [setIsPDNModalOpen])

    const handlePopoverClose = React.useCallback(() => {
        setPopoverAnchorEl(null)
        setpopoverVariant(null)
    }, [])

    const handleCopyModalOpen = React.useCallback(
        () => setCopyOrMoveModalOpen(true),
        []
    )
    const handleCopyModalClose = React.useCallback(
        () => setCopyOrMoveModalOpen(false),
        []
    )
    const [
        deleteProjectSearches,
        { loading: loadingDeleteSearches },
    ] = useMutation<any, { input: any }>(DeleteProductListing, {
        onCompleted: () => {
            resetPDNStore()
            if (getWindowURL().includes('showBackButton=false')) {
                window.close()
            }
            history.push(`/bpm/products/all?${getWindowQuery()}`)
        },
    })

    const handleDelete = React.useCallback(() => {
        NavPromptDialogContainer(
            JSON.stringify({
                title: 'Confirm Delete?',
                preferredMessage:
                    'You have chosen to delete this product, Continue?',
                buttonColor: '#d3455b',
                confirmText: 'Yes',
                cancelText: 'No',
            } as NavPromptMessageProps),
            (callbackState: boolean) => {
                if (!callbackState) return
                setTempPDNStore({ actionType: 'DELETE', value: [] })
                setIsPDNModalOpen(true)
            }
        )
    }, [productInfo])

    const handleSavePDN = useCallback(
        ({ noteType, longDescription, noteId, reason }: any) => {
            if (
                ['STATUSCHANGE', 'LISTINGTYPECHANGE'].includes(
                    tempPDNStore.actionType
                )
            ) {
                handleSaveNote?.({
                    noteType,
                    longDescription,
                    isEdit: false,
                    isTemp: true,
                    noteId,
                    reason,
                })
                onHeaderChanges(
                    tempPDNStore.value[0] as ListingHeaderVariants,
                    tempPDNStore.value[1]
                )
                return
            }

            if (tempPDNStore.actionType === 'DELETE') {
                deleteProjectSearches({
                    variables: {
                        input: {
                            listingId: productInfo?.listingId,
                            productDetailNote: {
                                listingId: productInfo?.listingId ?? '',
                                content: longDescription,
                                productDetailNoteType: noteType,
                                reason,
                                origin: getWindowDomain() + '/bpm',
                            },
                        },
                    },
                })
            }

            if (noteType === 'COPYMOVE') {
                const productDetail = copyMoveProductForm.sectionNames?.value?.split(
                    ' - '
                )

                const ptid = copyMoveProductForm.specAgentProductTypeId?.value
                const apiInput: ApiInput = {
                    listingId: productInfo?.listingId || '',
                    productType: copyMoveProductForm.productType?.value || '',
                    specAgentProductTypeId:
                        ptid && !isNaN(parseInt(ptid)) ? parseInt(ptid) : null,
                    sectionNumber: productDetail?.[0] || '',
                    sectionTitle: productDetail?.[1] || '',
                    isActive: copyMoveProductForm.isActive?.value === 'Active',
                    type:
                        copyMoveProductForm.listingType?.value === noChange
                            ? productInfo?.type || ''
                            : copyMoveProductForm.listingType?.value || '',
                    productName: productInfo?.productName || '',
                    productDetailNote: {
                        listingId: productInfo?.listingId ?? '',
                        content: longDescription,
                        productDetailNoteType: noteType,
                        reason,
                        origin: getWindowDomain() + '/bpm',
                    },
                    newState:
                        copyMoveProductForm.state?.value === noChange
                            ? productInfo?.submissionStatus || ''
                            : copyMoveProductForm.state?.value || '',
                    includeManufacturerOptions:
                        copyMoveProductForm.includeManufacturerOptions
                            ?.value === 'true',
                }
                if (tempPDNStore.actionType === 'MOVE') {
                    moveProduct({
                        variables: {
                            input: apiInput,
                        },
                    })
                } else {
                    copyProduct({
                        variables: {
                            input: apiInput,
                        },
                    })
                }
            }
        },
        [
            handleSaveNote,
            tempPDNStore,
            deleteProjectSearches,
            onHeaderChanges,
            productInfo?.listingId,
            productInfo?.type,
            productInfo?.productName,
            copyProduct,
            moveProduct,
            copyMoveProductForm.sectionNames?.value,
            copyMoveProductForm.isActive?.value,
        ]
    )

    const handleResetAddNote = useCallback(() => {
        setIsPDNModalOpen(false)
    }, [])

    const shouldShowFilterPill = React.useMemo(() => {
        return !(
            processEnv.REACT_APP_EnableListingTypeFilterPill === '1' &&
            (isBPMAdmin || isBPMAuthor)
        )
    }, [processEnv, isBPMAdmin, isBPMAuthor])

    return (
        <GridContainer
            justifyContent="space-between"
            className={classes.header}
            data-testid="listingHeaderContainer"
        >
            <GridItem className={classes.headerButtonWrapper}>
                <GridContainer className={classes.headerButtonContainer}>
                    {shouldShowFilterPill && (
                        <GridItem wrap="nowrap">
                            <span className={classes.headerLabel}>
                                LISTING TYPE
                            </span>
                            <GridContainer
                                item
                                data-testid="listingHeaderListingType"
                            >
                                <ListingHeaderButton
                                    buttonKey={'listingLevel'}
                                    minWidth={150}
                                    buttonLabel={listingLevel as string}
                                    isPopoverOpen={
                                        popoverVariant === 'listingLevel'
                                    }
                                    handleButtonClick={handleButtonClick}
                                    isOnEditMode={
                                        editMode && canInteractWithButtons
                                    }
                                />
                                <ListingHeaderPopover
                                    popoverContents={
                                        <ListingLevelVariant
                                            onChange={handleListingLevelChange}
                                        />
                                    }
                                    sectionLabel={'Listing Type'}
                                    isPopoverOpen={
                                        popoverVariant === 'listingLevel'
                                    }
                                    popoverAnchorEl={popoverAnchorEl}
                                    handlePopoverClose={handlePopoverClose}
                                />
                            </GridContainer>
                        </GridItem>
                    )}
                    <GridItem wrap="nowrap">
                        <span
                            className={clsx(
                                classes.headerLabel,
                                'state-component'
                            )}
                        >
                            STATE
                        </span>
                        <GridContainer item data-testid="listingHeaderState">
                            <ListingHeaderButton
                                buttonKey={'state'}
                                minWidth={150}
                                buttonLabel={submissionStatus as string}
                                isPopoverOpen={popoverVariant === 'state'}
                                handleButtonClick={handleButtonClick}
                                isOnEditMode={
                                    editMode && canInteractWithButtons
                                }
                            />
                            <ListingHeaderPopover
                                data-testid="stateEditmodeList"
                                popoverContents={
                                    <ListingStatusVariant
                                        onChange={handleStateChange}
                                        hideDeprecated={!isDeprecated}
                                    />
                                }
                                sectionLabel={'State'}
                                isPopoverOpen={popoverVariant === 'state'}
                                popoverAnchorEl={popoverAnchorEl}
                                handlePopoverClose={handlePopoverClose}
                            />
                        </GridContainer>
                    </GridItem>

                    {shouldShowFilterPill && (
                        <>
                            <GridItem wrap="nowrap">
                                <span className={classes.headerLabel}>
                                    STATUS
                                </span>
                                <GridContainer
                                    item
                                    data-testid="listingHeaderStatus"
                                >
                                    <div
                                        className={clsx(
                                            isDeltekPublishingAuthor &&
                                            classes.editPageButtonContainer
                                        )}
                                    >
                                        <ListingHeaderButton
                                            buttonKey={'status'}
                                            minWidth={100}
                                            buttonLabel={
                                                isActive ? 'Active' : 'Inactive'
                                            }
                                            isPopoverOpen={
                                                popoverVariant === 'status'
                                            }
                                            handleButtonClick={
                                                handleButtonClick
                                            }
                                            isOnEditMode={
                                                (editMode ||
                                                    isDeltekPublishingAuthor) &&
                                                canInteractWithButtons
                                            }
                                        />
                                    </div>
                                    <ListingHeaderPopover
                                        popoverContents={
                                            <ListingStateVariant
                                                onChange={handleStatusChange}
                                            />
                                        }
                                        sectionLabel={'Status'}
                                        isPopoverOpen={
                                            popoverVariant === 'status'
                                        }
                                        popoverAnchorEl={popoverAnchorEl}
                                        handlePopoverClose={handlePopoverClose}
                                    />
                                </GridContainer>
                            </GridItem>
                            <GridItem wrap="nowrap">
                                <span className={classes.headerLabel}>
                                    ASSIGN DELTEK
                                </span>
                                <>
                                    <ListingDetailsUserAssignment
                                        isDeltekAdmin={canInteractWithButtons}
                                        isPopoverOpen={
                                            popoverVariant === 'state'
                                        }
                                        isOnEditMode={editMode}
                                        poupHeaderText={'ASSIGN BPM'}
                                        assignedDeltekLabel={
                                            assignedDeltekLabel ?? 'None'
                                        }
                                        handleDeltekUsersChange={
                                            handleDeltekUsersChange
                                        }
                                        buttonVariant="Deltek"
                                        productInfo={productInfo}
                                        onCancelClick={onCancelClick}
                                        users={allDeltekUsers}
                                        canEdit={canEdit}
                                    />
                                </>
                            </GridItem>
                        </>
                    )}

                    <GridItem wrap="nowrap">
                        <span className={classes.headerLabel}>ASSIGN BPM</span>
                        <>
                            <ListingDetailsUserAssignment
                                isDeltekAdmin={
                                    !!(isDeltekAdmin || updateBPMLimited)
                                }
                                isPopoverOpen={popoverVariant === 'state'}
                                isOnEditMode={editMode}
                                poupHeaderText={'ASSIGN BPM'}
                                assignedBPMLabel={assignedBPMLabel ?? 'None'}
                                handleBPMUsersChange={handleBPMUsersChange}
                                buttonVariant="BPM"
                                productInfo={productInfo}
                                onCancelClick={onCancelClick}
                                users={allBPMUsers}
                                canEdit={canEdit}
                            />
                        </>
                    </GridItem>
                    <PDNButton
                        disabled={!!isAddProductActive}
                        isOnEditMode={editMode}
                        listingLevel={listingLevel}
                    />
                </GridContainer>
            </GridItem>
            <GridContainer
                style={{ flexGrow: 2 }}
                item
                md
                justifyContent="flex-end"
            >
                {(canEdit || updateSwatchbox) && !isDeltekPublishingAuthor && (
                    <>
                        {editMode ? (
                            <GridContainer
                                item
                                direction="row"
                                justifyContent={downMD ? undefined : 'flex-end'}
                            >
                                <div>
                                    <div>
                                        <span className={classes.headerLabel}>
                                            EDIT
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex' }}>
                                        <div
                                            className={clsx(
                                                classes.actionButtonContainer,
                                                classes.cancelButtonContainer,
                                                classes.headerButtonSpacing
                                            )}
                                        >
                                            <ActionButton
                                                data-testid="listingHeaderCancelButton"
                                                onClick={onCancelClick}
                                                color="secondary"
                                                loading={
                                                    saveProductListingDetailLoading
                                                }
                                            >
                                                Cancel
                                            </ActionButton>
                                        </div>
                                        <div
                                            className={clsx(
                                                classes.actionButtonContainer,
                                                (submit &&
                                                    classes.headerButtonSpacing) ||
                                                ''
                                            )}
                                        >
                                            <ActionButton
                                                data-testid="listingHeaderSaveButton"
                                                onClick={onSaveClick}
                                                loading={
                                                    saveProductListingDetailLoading
                                                }
                                                disabled={disableSave}
                                            >
                                                Save
                                            </ActionButton>
                                        </div>
                                        {submit && (
                                            <div
                                                className={clsx(
                                                    classes.actionButtonContainer
                                                )}
                                            >
                                                <ActionButton
                                                    data-testid="listingHeaderSubmitButton"
                                                    onClick={onPublishClick}
                                                    loading={
                                                        saveProductListingDetailLoading
                                                    }
                                                    disabled={disableSave}
                                                >
                                                    Submit
                                                </ActionButton>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </GridContainer>
                        ) : (
                            <GridContainer
                                item
                                direction="row"
                                justifyContent={downMD ? undefined : 'flex-end'}
                                className={clsx(
                                    add &&
                                    isDeltekAdmin &&
                                    classes.optionalButtonContainer
                                )}
                                data-testid="optionalButtons"
                            >
                                {add && (
                                    <div
                                        className={classes.headerButtonSpacing}
                                    >
                                        <div>
                                            <span
                                                className={classes.headerLabel}
                                            >
                                                EDIT
                                            </span>
                                        </div>
                                        <div
                                            className={clsx(
                                                classes.actionButtonContainer,
                                                classes.addProductButtonContainer
                                            )}
                                        >
                                            <ActionButton
                                                data-testid="listingHeaderAddProductListingButton"
                                                onClick={onAddProductClick}
                                            >
                                                Add Product
                                            </ActionButton>
                                        </div>
                                    </div>
                                )}
                                {isDeltekAdmin && !isDeprecated && (
                                    <>
                                        <div
                                            className={
                                                classes.headerButtonSpacing
                                            }
                                        >
                                            <div
                                                className={clsx(
                                                    classes.actionButtonContainer,
                                                    classes.editPageButtonContainer,
                                                    classes.mfgUpdateButton,
                                                    classes.dangerButtons
                                                )}
                                                data-testid="editButtonHolder"
                                            >
                                                <ActionButton
                                                    data-testid="listingHeaderMoveCopyButton"
                                                    onClick={
                                                        handleCopyModalOpen
                                                    }
                                                >
                                                    Move/Copy
                                                </ActionButton>
                                            </div>
                                        </div>
                                        <div
                                            className={
                                                classes.headerButtonSpacing
                                            }
                                        >
                                            <div
                                                className={clsx(
                                                    classes.actionButtonContainer,
                                                    classes.editPageButtonContainer,
                                                    classes.mfgUpdateButton,
                                                    classes.dangerButtons
                                                )}
                                                data-testid="editButtonHolder"
                                            >
                                                <ActionButton
                                                    data-testid="listingHeaderDeleteButton"
                                                    onClick={handleDelete}
                                                >
                                                    {loadingDeleteSearches ? (
                                                        <CircularProgress />
                                                    ) : (
                                                        'Delete'
                                                    )}
                                                </ActionButton>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {(update ||
                                    isDeprecated ||
                                    updateSwatchbox) && (
                                        <div
                                            className={classes.headerButtonSpacing}
                                        >
                                            {!add && (
                                                <div>
                                                    <span
                                                        className={
                                                            classes.headerLabel
                                                        }
                                                    >
                                                        EDIT
                                                    </span>
                                                </div>
                                            )}
                                            <div
                                                className={clsx(
                                                    classes.actionButtonContainer,
                                                    classes.editPageButtonContainer,
                                                    add && classes.mfgUpdateButton
                                                )}
                                                data-testid="editButtonHolder"
                                            >
                                                <ActionButton
                                                    data-testid="listingHeaderEditButton"
                                                    onClick={onEditClick}
                                                >
                                                    Update
                                                </ActionButton>
                                            </div>
                                        </div>
                                    )}
                            </GridContainer>
                        )}
                    </>
                )}
            </GridContainer>
            <CopyMoveDialog
                isLoading={!!loadingCopyProduct || !!loadingMoveProduct}
                copyOrMoveModalOpen={copyOrMoveModalOpen}
                productInfo={productInfo}
                setIsPDNModalOpen={setIsPDNModalOpen}
                setTempPDNStore={setTempPDNStore}
                productListingDetailFormProp={productListingDetailFormProp}
                handleCopyModalClose={handleCopyModalClose}
                setCopyMoveProductForm={setCopyMoveProductForm}
                copyMoveProductForm={copyMoveProductForm}
            />
            <AddNotesModal
                isEditMode={!!editMode}
                open={!!isPDNModalOpen}
                onSave={handleSavePDN}
                onCancel={handleCancelCreatePDN}
                noteType={
                    typeAction[
                    tempPDNStore.actionType as keyof typeof typeAction
                    ] as NoteType
                }
                action={
                    tempPDNStore.actionType as keyof typeof noteReasonLabels
                }
                resetAction={
                    ['DELETE', 'STATUSCHANGE', 'LISTINGTYPECHANGE'].includes(
                        tempPDNStore.actionType
                    )
                        ? handleResetAddNote
                        : undefined
                }
            />
        </GridContainer>
    )
}
